/**
 * The frame limit (ADR-093 decisions 40 to 45): how many frames a second the page draws, as a player chooses it, and
 * the pacer that holds the page to it. The pacer is told the time of every animation frame the browser delivers and
 * answers whether to draw that one. It also reads the display's rate from the gaps between those frames, which the
 * row needs to offer 120 only on a display faster than that. It reads no clock and touches no browser object, so the
 * page and its tests drive it with the same times.
 */

/**
 * A choice: 30, 60 or 120 frames a second, or `match`, which draws every frame the display asks for.
 */
export type FrameLimit = "30" | "60" | "120" | "match";

/**
 * The choices in the order the row shows them.
 */
export const FRAME_LIMITS: readonly FrameLimit[] = Object.freeze(["30", "60", "120", "match"] as const);

/**
 * The choice a viewer has before choosing: 60 (ADR-093 decision 40).
 */
export const DEFAULT_FRAME_LIMIT: FrameLimit = "60";

/**
 * 120 is offered only on a display that refreshes faster than this, in Hz.
 */
export const FAST_DISPLAY_HZ = 100;

/**
 * How many intervals between frames the display's rate is taken from, and how often it is taken again, in frames.
 */
export const REFRESH_SAMPLE = 30;

/**
 * The fewest kept intervals a rate is taken from.
 */
export const REFRESH_MIN_KEPT = 20;

/**
 * An interval of this many ms or more is not kept: a hidden tab, a hitch, or a window dragged between screens.
 */
export const REFRESH_GAP_MS = 100;

/**
 * The rates a measured rate is read as when it is within `REFRESH_SNAP` of one.
 */
export const COMMON_REFRESH_RATES: readonly number[] = Object.freeze([30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 240]);

/**
 * How near a common rate a measured one must be to read as it, as a share of the common rate.
 */
export const REFRESH_SNAP = 0.04;

/**
 * How far from the choice, as a share of it, a whole number of display frames may leave the frame rate and still be
 * drawn evenly.
 */
export const EVEN_TOLERANCE = 0.1;

/**
 * The credit a frame needs to be drawn, less a hair so a sum of fractions that should reach one does.
 */
const DRAW_CREDIT = 1 - 1e-9;

/**
 * How a choice is drawn on a display. `off` draws every frame; `even` draws every `every`th frame, so the frames are
 * all one length; `average` draws a frame when a credit of `step` a display frame reaches one, so the rate is the
 * choice and the frames alternate between two lengths. `fps` is the rate drawn, null while the display's is unknown.
 */
export type FramePlan = { readonly kind: "off"; readonly fps: number | null } | { readonly kind: "even"; readonly every: number; readonly step: number; readonly fps: number } | { readonly kind: "average"; readonly step: number; readonly fps: number };

/**
 * Reads a stored value as a choice.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The choice, or 60 for anything that is not exactly `30`, `60`, `120` or `match`.
 */
export function parseFrameLimit(raw: string | null): FrameLimit {
  return raw === "30" || raw === "60" || raw === "120" || raw === "match" ? raw : DEFAULT_FRAME_LIMIT;
}

/**
 * Whether the row offers a choice on a display.
 *
 * @param limit The choice.
 * @param refresh The display's measured rate in Hz, or null before it is measured.
 * @returns False only for 120 on a display not measured faster than `FAST_DISPLAY_HZ`.
 */
export function frameLimitOffered(limit: FrameLimit, refresh: number | null): boolean {
  return limit !== "120" || (refresh !== null && refresh > FAST_DISPLAY_HZ);
}

/**
 * A measured rate as the rate the page paces for.
 *
 * @param hz The measured rate in Hz.
 * @returns The nearest of `COMMON_REFRESH_RATES` when within `REFRESH_SNAP` of it, else the rate rounded.
 */
export function snapRefresh(hz: number): number {
  let nearest = COMMON_REFRESH_RATES[0] as number;
  for (const rate of COMMON_REFRESH_RATES) {
    if (Math.abs(rate - hz) < Math.abs(nearest - hz)) {
      nearest = rate;
    }
  }
  return Math.abs(nearest - hz) <= nearest * REFRESH_SNAP ? nearest : Math.round(hz);
}

/**
 * The display's rate from the intervals between its frames.
 *
 * @param intervals Intervals in ms.
 * @returns The rate snapped by `snapRefresh`, from the median of the intervals that are positive and under
 *   `REFRESH_GAP_MS`; null when fewer than `REFRESH_MIN_KEPT` are kept.
 */
export function refreshFromIntervals(intervals: readonly number[]): number | null {
  const kept = intervals.filter((interval) => interval > 0 && interval < REFRESH_GAP_MS).sort((a, b) => a - b);
  if (kept.length < REFRESH_MIN_KEPT) {
    return null;
  }
  return snapRefresh(1000 / (kept[Math.floor(kept.length / 2)] as number));
}

/**
 * How a choice is drawn on a display (ADR-093 decision 42).
 *
 * @param limit The choice.
 * @param refresh The display's rate in Hz, or null when not yet measured.
 * @returns `off` for Match display, an unmeasured display and a choice at or above the display's rate; `even` when a
 *   whole number of display frames, 2 or more, leaves the rate within `EVEN_TOLERANCE` of the choice; else `average`.
 */
export function framePlan(limit: FrameLimit, refresh: number | null): FramePlan {
  if (refresh === null) {
    return { kind: "off", fps: null };
  }
  if (limit === "match" || Number(limit) >= refresh) {
    return { kind: "off", fps: refresh };
  }
  const cap = Number(limit);
  const every = Math.round(refresh / cap);
  const rate = refresh / every;
  if (every >= 2 && Math.abs(rate / cap - 1) <= EVEN_TOLERANCE) {
    return { kind: "even", every, step: 1 / every, fps: rate };
  }
  return { kind: "average", step: cap / refresh, fps: cap };
}

/**
 * Holds the page to a choice. `tick` is called once for every animation frame, whether or not it is drawn, so the
 * display's rate is read from frames the pacer itself does not skip.
 */
export class FramePacer {
  /**
   * Called when the display's rate is first taken and whenever it changes; the owner reads `refresh`.
   */
  public onRefresh: (() => void) | null = null;

  private chosen: FrameLimit;
  private rate: number | null = null;
  private current: FramePlan = { kind: "off", fps: null };
  private credit = 0;
  private last = -1;
  private readonly ring: number[] = new Array<number>(REFRESH_SAMPLE).fill(0);
  private next = 0;
  private kept = 0;
  private since = 0;

  /**
   * @param limit The choice in force.
   */
  public constructor(limit: FrameLimit) {
    this.chosen = limit;
  }

  /**
   * The choice in force.
   *
   * @returns The choice.
   */
  public get limit(): FrameLimit {
    return this.chosen;
  }

  /**
   * The display's rate, snapped, or null until it is first taken.
   *
   * @returns The rate in Hz.
   */
  public get refresh(): number | null {
    return this.rate;
  }

  /**
   * How the choice is drawn on the display as measured.
   *
   * @returns The plan.
   */
  public get plan(): FramePlan {
    return this.current;
  }

  /**
   * Puts a new choice in force from the next frame.
   *
   * @param limit The choice.
   */
  public setLimit(limit: FrameLimit): void {
    this.chosen = limit;
    this.replan();
  }

  /**
   * Takes one animation frame.
   *
   * @param time The frame's time in ms, as the browser gives it to an animation frame callback.
   * @returns True when the frame is to be drawn. The frame after a gap of `REFRESH_GAP_MS` or more is always drawn,
   *   and starts the measurement of the display's rate again, keeping the plan in force meanwhile.
   */
  public tick(time: number): boolean {
    const previous = this.last;
    this.last = time;
    let gap = false;
    if (previous >= 0) {
      const interval = time - previous;
      if (interval >= REFRESH_GAP_MS) {
        gap = true;
        this.kept = 0;
        this.since = 0;
        this.next = 0;
      } else if (interval > 0) {
        this.ring[this.next] = interval;
        this.next = (this.next + 1) % REFRESH_SAMPLE;
        this.kept = Math.min(this.kept + 1, REFRESH_SAMPLE);
        this.since += 1;
        if (this.since >= REFRESH_SAMPLE) {
          this.since = 0;
          this.measure();
        }
      }
    }
    if (this.current.kind === "off") {
      this.credit = 0;
      return true;
    }
    if (gap) {
      this.credit = 0;
      return true;
    }
    this.credit += this.current.step;
    if (this.credit >= DRAW_CREDIT) {
      this.credit -= 1;
      return true;
    }
    return false;
  }

  /**
   * Takes the display's rate from the kept intervals, and plans again when it has changed.
   */
  private measure(): void {
    const taken = refreshFromIntervals(this.ring.slice(0, this.kept));
    if (taken === null || taken === this.rate) {
      return;
    }
    this.rate = taken;
    this.replan();
    this.onRefresh?.();
  }

  /**
   * Works out the plan for the choice and the rate in force.
   */
  private replan(): void {
    this.current = framePlan(this.chosen, this.rate);
    this.credit = Math.min(this.credit, 1 - 1e-6);
  }
}
